




































































































import { Component, Vue, Ref, Watch } from "vue-property-decorator";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";

import PickerDialogComponent from "@/components/common/PickerDialog.vue";
import ControlConfigComponent from "@/components/combox/ControlConfig.vue";
import MultiSelectionPickerComponent from "@/components/common/MultiSelectionPicker.vue";
import {
  ComBoxClientConfig,
  ComBoxClientConfigInfo,
  DEFAULT_DRYING_PROGRAM_MINI,
  DEFAULT_SELECTABLE_DRYING_PROGRAMS_BOX,
  DEFAULT_SELECTABLE_DRYING_PROGRAMS_MINI,
  getComBoxClientConfig,
} from "@/data/models/ComBoxClientConfig";
import { ComBoxHumidityUnit } from "@/data/models/ComBoxHumidityUnit";
import { ComBoxSensorUsage } from "@/data/models/ControlConfig";
import { ComBoxDryingProgram, MINI_SUPPORTED_DRYING_PROGRAMS } from "@/data/models/ComBoxDryingProgram";
import { getComBoxProgramName, calculateSlotNumberFromIndex } from "@/data/models/ComBox";
import { ROOM_SENSOR_INDEX } from "@/data/models/ComBoxSensorData";
import ConfirmationDialogComponent from "../common/ConfirmationDialog.vue";
import { filterOutUndefined } from "@/utils/Utils";

@Component({
  components: {
    picker: PickerDialogComponent,
    "multi-selection-picker": MultiSelectionPickerComponent,
    "base-dialog": BaseDialogComponent,
    "control-config": ControlConfigComponent,
    "confirmation-dialog": ConfirmationDialogComponent,
  },
})
export default class CreateClientConfigComponent extends Vue {
  @Ref("confirmation")
  confirmationDialogComponent!: ConfirmationDialogComponent;

  name = "";
  version = "";
  displayMessage = "";

  hasChanged = false;
  checkForSelectableProgramsChange = false;

  errors: string[] = [];

  humidityUnit = ComBoxHumidityUnit.RELATIVE_HUMIDITY;
  humidityUnitOptions = [
    ComBoxHumidityUnit.RELATIVE_HUMIDITY,
    ComBoxHumidityUnit.MIXING_RATIO,
    ComBoxHumidityUnit.ABSOLUTE_HUMIDITY,
  ];

  hiddenHumidityUnitOptions = [
    ComBoxHumidityUnit.MIXING_RATIO,
    ComBoxHumidityUnit.ABSOLUTE_HUMIDITY,
  ];

  get humidityUnitFormatter() {
    return (humidityUnit: ComBoxHumidityUnit) => {
      switch (humidityUnit) {
        case ComBoxHumidityUnit.ABSOLUTE_HUMIDITY:
          return this.$t(
            "settings.comBoxHumidityUnit.absoluteHumidity"
          ).toString();
        case ComBoxHumidityUnit.MIXING_RATIO:
          return this.$t("settings.comBoxHumidityUnit.mixingRatio").toString();
        case ComBoxHumidityUnit.RELATIVE_HUMIDITY:
          return this.$t(
            "settings.comBoxHumidityUnit.relativeHumidity"
          ).toString();
      }
    };
  }

  expertModeEnabled = true;
  expertModeEnabledOptions = [false, true];
  get expertModeEnabledFormatter() {
    return (enabled: boolean) => {
      return (enabled
        ? this.$t("common.on")
        : this.$t("common.off")
      ).toString();
    };
  }

  selectedSensors: number[] = [];
  selectedSensorsOptions = [0, 1, 2, 3, 5, 6, 7, 8];

  hiddenHumidityUnit: ComBoxHumidityUnit | undefined = ComBoxHumidityUnit.ABSOLUTE_HUMIDITY;

  initialSelectablePrograms: number[] = [];
  initialSelectableProgramsMini: number[] = [];
  initialDefaultDryingProgramMini = DEFAULT_DRYING_PROGRAM_MINI;

  defaultDryingProgramMini = DEFAULT_DRYING_PROGRAM_MINI;

  get selectedSensorsFormatter() {
    return (sensorIndex: number) => {
      return this.$t("comBoxClientConfig.sensor", [calculateSlotNumberFromIndex(sensorIndex)]);
    };
  }

  selectableDryingPrograms = [...DEFAULT_SELECTABLE_DRYING_PROGRAMS_BOX];
  selectableDryingProgramsOptions = [0, 1, 2, 3, 4, 5/* , 6, 7 */];

  selectableDryingProgramsMini = [...DEFAULT_SELECTABLE_DRYING_PROGRAMS_MINI];
  selectableDryingProgramsMiniOptions = [...MINI_SUPPORTED_DRYING_PROGRAMS];

  get selectableDryingProgramsFormatter() {
    return (programIndex: number) => {
      return getComBoxProgramName(this, programIndex);
    };
  }

  selectableSensorPrograms = [0];
  selectableSensorProgramsOptions = [0, 1, 2, 3, 4, 5/* , 6, 7 */];
  get selectableSensorProgramsFormatter() {
    return (programIndex: number) => {
      return this.$t("comBoxClientConfig.formatSensorProgram", [
        programIndex + 1,
      ]).toString();
    };
  }

  selectableComBoxDryingPrograms: ComBoxDryingProgram[] = this.selectableDryingProgramsOptions.map(
    (programNumber) => {
      return {
        name: programNumber.toString(),
        programNumber: programNumber,
        locked: false,
        needsReferenceSensor: false,
      };
    }
  );

  @Ref("baseDialog")
  baseDialogComponent!: BaseDialogComponent;

  @Ref("controlConfig")
  controlConfigComponent!: ControlConfigComponent;

  @Ref("selectedSensorsPicker")
  selectedSensorsPickerComponent!: MultiSelectionPickerComponent<number>;

  confirmationMessage = "";
  startComBox = true;

  resolve?: (value: ComBoxClientConfig) => void = undefined;
  reject?: (reason: string) => void = undefined;

  clientConfigToEdit: ComBoxClientConfig | null = null;

  onDialogOpened() {
    const clientConfigToEdit = this.clientConfigToEdit;

    if (clientConfigToEdit) {
      this.$nextTick(() => {
        this.controlConfigComponent.displayControlConfig(
          clientConfigToEdit.defaultControlConfig
        );
      });
    }
  }

  @Watch("selectableDryingPrograms")
  onSelectableDryingProgramsChanged() {
    if (this.checkForSelectableProgramsChange) {
      if (this.selectableDryingPrograms.length > this.initialSelectablePrograms.length) {
        this.selectableDryingPrograms.forEach((it) => {
          if (!this.initialSelectablePrograms.includes(it)) {
            this.hasChanged = true;
          }
        });
      }
      else {
        this.initialSelectablePrograms.forEach((it) => {
          if (!this.selectableDryingPrograms.includes(it)) {
            this.hasChanged = true;
          }
        });
      }
    }
  }

  @Watch("selectableDryingProgramsMini")
  onSelectableDryingProgramsMiniChanged() {
    if (this.checkForSelectableProgramsChange) {
      if (this.selectableDryingProgramsMini.length > this.initialSelectableProgramsMini.length) {
        this.selectableDryingProgramsMini.forEach((it) => {
          if (!this.initialSelectableProgramsMini.includes(it)) {
            this.hasChanged = true;
          }
        });
      }
      else {
        this.initialSelectableProgramsMini.forEach((it) => {
          if (!this.selectableDryingProgramsMini.includes(it)) {
            this.hasChanged = true;
          }
        });
      }
    }
  }

  @Watch("hasChanged") 
  onHasChangedWatcher() {
    this.$emit("hasConfigurationChanged", this.hasChanged);
    this.baseDialogComponent.negativeButtonText = this.negativeButtonText;
  }

  @Watch("humidityUnitOptions") 
  onHumidityUnitOptionsChanged() {
    this.errors = [];
    if (this.hiddenHumidityUnit && this.humidityUnit === this.hiddenHumidityUnit) {
      this.errors.push(
        this.$t("comBoxClientConfig.invalidHumidityUnitError").toString()
      );
    }
  }

  onHasChanged(hasChanged: boolean) {
    if (hasChanged) {
      this.hasChanged = true;
    }
  }

  onExpertModeToggle(expertModeEnabled: boolean) {
    if (expertModeEnabled !== this.expertModeEnabled) {
      this.hasChanged = true;
    }
  }

  onHumidityUnitChanged(humidityUnit: ComBoxHumidityUnit) {
    if (
      humidityUnit !== this.humidityUnit &&
      humidityUnit !== this.clientConfigToEdit?.hiddenHumidityUnit
    ) {
      this.hasChanged = true;
      this.errors = [];
    }
  }

  recalculateHumidityUnitOptions(hiddenHumidityUnit: ComBoxHumidityUnit) {
    this.errors = [];
    this.humidityUnitOptions = Object.values(ComBoxHumidityUnit).filter((option) => option !== hiddenHumidityUnit);
  }

  onHiddenHumidityUnitChanged(hiddenHumidityUnit: ComBoxHumidityUnit) {
    if (hiddenHumidityUnit !== this.hiddenHumidityUnit) {
      this.hasChanged = true;
      this.recalculateHumidityUnitOptions(hiddenHumidityUnit);
    }
  }

  onDefaultDryingProgramMiniChanged(program: number) {
    this.defaultDryingProgramMini = program;
    if (program !== this.initialDefaultDryingProgramMini) {
      this.hasChanged = true;
    }
  }

  get negativeButtonText() {
    return this.$t(this.hasChanged ? "common.abort" : "common.close").toString();
  }

  async show(clientConfigInfo?: ComBoxClientConfigInfo, hasChanged?: boolean, clientConfigInfoMini?: ComBoxClientConfigInfo) {
    if (hasChanged !== undefined) {
      this.hasChanged = hasChanged;
    }
    
    if (clientConfigInfo) {
      this.selectedSensors = [];
      this.selectableSensorPrograms = [];
      this.selectableDryingPrograms = [];
      this.selectableDryingProgramsMini = [];

      const clientConfigToEdit = getComBoxClientConfig(clientConfigInfo);

      this.clientConfigToEdit = clientConfigToEdit;

      this.name = "";
      this.version = clientConfigToEdit.version.toString() ?? "";
      this.displayMessage = clientConfigToEdit.displayMessage ?? "";
      this.expertModeEnabled = clientConfigToEdit.expertModeEnabled;
      this.humidityUnit = clientConfigToEdit.humidityUnit;
      this.hiddenHumidityUnit = clientConfigToEdit.hiddenHumidityUnit;

      if (this.hiddenHumidityUnit) {
        this.recalculateHumidityUnitOptions(this.hiddenHumidityUnit);
      }

      clientConfigToEdit.defaultControlConfig.sensorUsage.forEach(
        (usage, index) => {
          if (index !== ROOM_SENSOR_INDEX && usage == ComBoxSensorUsage.PROGRAM) {
            this.selectedSensors.push(index);
          }
        }
      );

      clientConfigToEdit.selectableDryingPrograms.forEach(
        (isSelected, index) => {
          if (isSelected) {
            this.selectableDryingPrograms.push(index);
          }
        }
      );

      if (clientConfigInfoMini) {
        const miniConfigToEdit = getComBoxClientConfig(clientConfigInfoMini, {
          isMini: true,
        });
        miniConfigToEdit.selectableDryingPrograms.forEach(
          (isSelected, index) => {
            if (isSelected) {
              this.selectableDryingProgramsMini.push(index);
            }
          }
        );
        this.defaultDryingProgramMini = miniConfigToEdit.defaultControlConfig.dryingProgram;
      } else if (clientConfigToEdit.selectableDryingProgramsMini) {
        clientConfigToEdit.selectableDryingProgramsMini.forEach(
          (isSelected, index) => {
            if (isSelected) {
              this.selectableDryingProgramsMini.push(index);
            }
          }
        );
      }

      if (this.selectableDryingProgramsMini.length === 0) {
        this.selectableDryingProgramsMini = [...DEFAULT_SELECTABLE_DRYING_PROGRAMS_MINI];
        this.defaultDryingProgramMini = DEFAULT_DRYING_PROGRAM_MINI;
      }

      clientConfigToEdit.selectableSensorPrograms.forEach(
        (isSelected, index) => {
          if (isSelected) {
            this.selectableSensorPrograms.push(index);
          }
        }
      );
    } else {
      this.clientConfigToEdit = null;
      this.selectedSensors = [0, 1, 2, 3];
      this.selectableSensorPrograms = [0, 1, 2, 3, 4, 5/* , 6, 7 */];
      this.selectableDryingProgramsMini = [...DEFAULT_SELECTABLE_DRYING_PROGRAMS_MINI];
      this.defaultDryingProgramMini = DEFAULT_DRYING_PROGRAM_MINI;
    }

    // Clone arrays so change detection compares against immutable snapshots.
    this.initialSelectablePrograms = [...this.selectableDryingPrograms];
    this.initialSelectableProgramsMini = [...this.selectableDryingProgramsMini];
    this.initialDefaultDryingProgramMini = this.defaultDryingProgramMini;
    this.checkForSelectableProgramsChange = true;

    this.baseDialogComponent.show({
      styleAsAlertDialog: true,
      title: this.$t("comBoxClientConfig.createClientConfig").toString(),
      negativeButtonText: this.negativeButtonText,
      positiveButtonText: this.$t("common.save").toString(),
      closeOnSave: false,
      persistent: true,
    });

    return new Promise<ComBoxClientConfig>((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
    });
  }

  async onSave() {
    const clientConfig = this.collectClientConfig();
    this.resolve && this.resolve(clientConfig);
    this.baseDialogComponent.isShowing = false;
  }

  async onCancel() {
    if (this.hasChanged) {
      const result = await this.confirmationDialogComponent.show({
        title: this.$t("common.confirmationDialog.cancelTitle").toString(),
        message: this.$t("common.confirmationDialog.cancelMessage").toString(),
      });

      if (result) {
        this.hasChanged = false;
        this.baseDialogComponent.isShowing = false;
      }
    }
    else {
      this.hasChanged = false;
      this.baseDialogComponent.isShowing = false;
    }
  }

  collectClientConfig(): ComBoxClientConfig {
    const controlConfig = this.controlConfigComponent.collectControlConfig();

    controlConfig.sensorUsage = [
      ...[0, 1, 2, 3].map((sensorIndex) =>
        this.selectedSensors.indexOf(sensorIndex) >= 0
          ? ComBoxSensorUsage.PROGRAM
          : ComBoxSensorUsage.MEASUREMENT
      ),
      ComBoxSensorUsage.MEASUREMENT,
      ...[5, 6, 7, 8].map((sensorIndex) =>
        this.selectedSensors.indexOf(sensorIndex) >= 0
          ? ComBoxSensorUsage.PROGRAM
          : ComBoxSensorUsage.MEASUREMENT
      ),
    ];

    const clientConfig: ComBoxClientConfig = filterOutUndefined({
      name: this.name,
      humidityUnit: this.humidityUnit,
      version: this.version ? Number.parseInt(this.version) : 0,
      displayMessage: this.displayMessage,
      selectableDryingPrograms: this.selectableDryingProgramsOptions.map(
        (programIndex) =>
          this.selectableDryingPrograms.indexOf(programIndex) >= 0
      ),
      selectableDryingProgramsMini: this.selectableDryingProgramsOptions.map(
        (programIndex) =>
          this.selectableDryingProgramsMini.indexOf(programIndex) >= 0
      ),
      defaultDryingProgramMini: this.defaultDryingProgramMini,
      selectableSensorPrograms: this.selectableSensorProgramsOptions.map(
        (programIndex) =>
          this.selectableSensorPrograms.indexOf(programIndex) >= 0
      ),
      expertModeEnabled: this.expertModeEnabled,
      defaultControlConfig: controlConfig,
      hiddenHumidityUnit: this.hiddenHumidityUnit
    });

    return clientConfig;
  }
}
